import { IS_EYEJACK_WEBXR_POLYFILL } from "../utils/utils";
import { WSBridgeCameraData, WSBridgeFrameData, WSBridgeAnchorData, WSBridgeMeshData, WSBridgePlaneData, WSBridgeFrameMetadata, WSBridgeDataUpdatePolicy, WSBridgeCameraImageType } from "./bridge.ws.types";

declare global {
  interface Window {
    EJUpdateCameraData?: (data?:WSBridgeCameraData) => void;
    EJFrameStart?: () => void;
    EJFrameEnd?: () => void;
  }
}

export interface WSBridgeOptions {
  frameStartCallback?: () => void | null;
  frameEndCallback?: () => void | null;
  //
  cameraUpdateCallback?: (data: WSBridgeCameraData, image: HTMLImageElement | ImageBitmap) => void;
  //
  planeAddedCallback?: (data: WSBridgePlaneData[]) => void;
  planeUpdatedCallback?: (data: WSBridgePlaneData[]) => void;
  planeRemovedCallback?: (data: WSBridgePlaneData[]) => void;
  //
  meshAddedCallback?: (data: WSBridgeMeshData[]) => void;
  meshUpdatedCallback?: (data: WSBridgeMeshData[]) => void;
  meshRemovedCallback?: (data: WSBridgeMeshData[]) => void;
}

export default class WSBridge {

  private options: WSBridgeOptions | null = null;
  private supported : boolean = true;
  private cameraCanvas: HTMLCanvasElement;
  private _cameraCanvasVisible: boolean = true;
  private cameraContextBitmap: ImageBitmapRenderingContext | null = null;
  private cameraContext2d: CanvasRenderingContext2D | null = null;

  private worker?: Worker | null = null;
  private queueCameraData: WSBridgeFrameData[] = [];
  private queuePlaneData: WSBridgeFrameData[] = [];
  private queueMeshData: WSBridgeFrameData[] = [];
  private textDecoder = new TextDecoder();
  private cameraCanvasUpdated = false;

  private _cameraDataUpdatePolicy: WSBridgeDataUpdatePolicy = WSBridgeDataUpdatePolicy.Always;
  private _cameraImageType: WSBridgeCameraImageType = WSBridgeCameraImageType.JPEG;
  private _cameraImageScale: number = 0.25;
  private _cameraImageQuality: number = 0.75;

  private _planeDataUpdatePolicy: WSBridgeDataUpdatePolicy = WSBridgeDataUpdatePolicy.None;
  private _planeData: WSBridgePlaneData[] = [];
  
  private _meshDataUpdatePolicy: WSBridgeDataUpdatePolicy = WSBridgeDataUpdatePolicy.None;
  private _meshData: WSBridgeMeshData[] = [];
  
  constructor(options?: WSBridgeOptions) {
    this.supported = IS_EYEJACK_WEBXR_POLYFILL;
    this.cameraCanvas = <HTMLCanvasElement>( document.getElementById('ejx-camera-canvas') );

    if( !this.supported ) {
      if( this.cameraCanvas ) {
        this.cameraCanvas.remove(); // delete the cameraCanvas element 
      }
      return;
    }
    if( this.cameraCanvas ) {
      this.options = options || null;
      window.EJFrameStart = this.frameStart.bind(this);
      window.EJFrameEnd = this.frameEnd.bind(this);
    } else {
      throw new Error("WSBridge.constructor - ejx-camera-canvas not found in dom.");
    }
  }

  //---------------------------------------------------------- init / kill.
  public init() {
    if (!this.supported) {
      console.warn("WSBridge.init: not supported.");
      return;
    }
    this.initWebSocketWorker();
  }

  public kill() {
    this.killWebSocketWorker();
  }

  //---------------------------------------------------------- camera canvas.
  public getCameraCanvas(): HTMLCanvasElement {
    return this.cameraCanvas;
  }

  public getCameraCanvasUpdated(): boolean {
    const value = this.cameraCanvasUpdated;
    this.cameraCanvasUpdated = false;
    return value;
  }

  //---------------------------------------------------------- camera canvas visible.
  public set cameraCanvasVisible( value:boolean ) {
    this._cameraCanvasVisible = value;
    this.cameraCanvas.style.opacity = value ? '1' : '0';
  }

  public get cameraCanvasVisible() {
    return this._cameraCanvasVisible;
  }

  //---------------------------------------------------------- plane data update policy.
  public set cameraDataUpdatePolicy( value:WSBridgeDataUpdatePolicy ) {
    this._cameraDataUpdatePolicy = value;
    this.worker?.postMessage({ type: "SET_CAMERA_DATA_UPDATE_POLICY", value: value });
  }

  public get cameraDataUpdatePolicy() {
    return this._cameraDataUpdatePolicy;
  }

  //---------------------------------------------------------- plane data update policy.
  public set planeDataUpdatePolicy( value:WSBridgeDataUpdatePolicy ) {
    this._planeDataUpdatePolicy = value;
    this.worker?.postMessage({ type: "SET_PLANE_DATA_UPDATE_POLICY", value: value });
  }

  public get planeDataUpdatePolicy() {
    return this._planeDataUpdatePolicy;
  }

  //---------------------------------------------------------- mesh data update policy.
  public set meshDataUpdatePolicy( value:WSBridgeDataUpdatePolicy ) {
    this._meshDataUpdatePolicy = value;
    this.worker?.postMessage({ type: "SET_MESH_DATA_UPDATE_POLICY", value: value });
  }

  public get meshDataUpdatePolicy() {
    return this._meshDataUpdatePolicy;
  }

  //---------------------------------------------------------- camera image type.
  public set cameraImageType( value:WSBridgeCameraImageType ) {
    const valueClamped = Math.max(WSBridgeCameraImageType.JPEG, Math.min(WSBridgeCameraImageType.PNG, value));
    this._cameraImageType = valueClamped;
    this.worker?.postMessage({ type: "SET_CAMERA_IMAGE_TYPE", value: valueClamped });
  }

  public get cameraImageType() {
    return this._cameraImageType;
  }

  //---------------------------------------------------------- camera image scale.
  public set cameraImageScale( value:number ) {
    const valueClamped = Math.max(0, Math.min(1, value));
    this._cameraImageScale = valueClamped;
    this.worker?.postMessage({ type: "SET_CAMERA_IMAGE_SCALE", value: valueClamped });
  }

  public get cameraImageScale() {
    return this._cameraImageScale;
  }

  //---------------------------------------------------------- camera image quality.
  public set cameraImageQuality( value:number ) {
    const valueClamped = Math.max(0, Math.min(1, value));
    this._cameraImageQuality = valueClamped;
    this.worker?.postMessage({ type: "SET_CAMERA_IMAGE_QUALITY", value: valueClamped });
  }

  public get cameraImageQuality() {
    return this._cameraImageQuality;
  }

  //---------------------------------------------------------- web worker.
  private initWebSocketWorker() {
    if( this.worker ) {
      return;
    }
    const workerUrl = new URL("/js/wsWorker.js", window.location.origin).href;
    this.worker = new Worker( workerUrl );
    this.worker.onmessage = (evt) => this.handleWorkerMessage(evt);
    this.worker.postMessage({ type: "SET_CAMERA_DATA_UPDATE_POLICY", value: this.cameraDataUpdatePolicy });
    this.worker.postMessage({ type: "SET_PLANE_DATA_UPDATE_POLICY", value: this.planeDataUpdatePolicy });
    this.worker.postMessage({ type: "SET_MESH_DATA_UPDATE_POLICY", value: this.meshDataUpdatePolicy });
    this.worker.postMessage({ type: "SET_CAMERA_IMAGE_TYPE", value: this.cameraImageType });
    this.worker.postMessage({ type: "SET_CAMERA_IMAGE_SCALE", value: this.cameraImageScale });
    this.worker.postMessage({ type: "SET_CAMERA_IMAGE_QUALITY", value: this.cameraImageQuality });
    this.worker.postMessage({ type: "INIT" });
  }

  private killWebSocketWorker() {
    if( this.worker ) {
      this.worker.postMessage({ type: "KILL" });
      this.worker.terminate();
      this.worker = null;
    }
  }

  private handleWorkerMessage(evt: MessageEvent) {
    const { type } = evt.data;
     if (type === "WS_OPEN") {
      console.log("[Main] WebSocket opened");
    } else if (type === "WS_ERROR") {
      console.error("[Main] WebSocket error:", evt.data.error);
    } else if (type === "WS_CLOSED") {
      console.log("[Main] WebSocket closed.");
    } else if (type === "FRAME_DATA") {
      const { frameBuffer, metadata, imageBitmap } = evt.data;
      this.processFrameDataMessage(frameBuffer, metadata, imageBitmap);
    }
  }

  private async processFrameDataMessage( frameBuffer:ArrayBuffer, metadata?:WSBridgeFrameMetadata, imageBitmap?:ImageBitmap ) {
    const frame: WSBridgeFrameData = {};
    //
    const dataView = new DataView(frameBuffer);
    const jsonByteStart = 4;
    const jsonByteLength = dataView.getUint32(0, false);
    const imageByteStart = jsonByteStart + jsonByteLength;
    const imageByteLength = frameBuffer.byteLength - imageByteStart;
    const imageSent = imageByteLength > 0;
    //
    if( metadata ) {
      frame.metadata = metadata;
    } else {
      try {
        if (jsonByteStart + jsonByteLength > frameBuffer.byteLength) {
          throw new Error("Invalid JSON length, skipping frame.");
        }
        const jsonBytes = new Uint8Array(frameBuffer, jsonByteStart, jsonByteLength);
        const jsonString = this.textDecoder.decode(jsonBytes);
        const jsonObj = JSON.parse(jsonString);
        frame.metadata = jsonObj;
      } catch (error) {
        console.error(error);
        return;
      }
    }
    if( frame.metadata!.planeData ) {
      this.queuePlaneData.push(frame);
    }
    if( frame.metadata!.meshData ) {
      this.queueMeshData.push(frame);
    }
    //
    if( !frame.metadata!.cameraData ) {
      return;
    }
    if( imageBitmap ) { // already been created in worker.
      frame.imageBitmap = imageBitmap;
      this.queueCameraData.push(frame);
      return;
    }
    try {
      let createImage = true;
      createImage = createImage && imageSent;
      createImage = createImage && frame.metadata!.cameraData !== undefined;
      if (createImage) {
        const imageType = frame.metadata!.cameraData!.imageType;
        const imageMimeType = imageType === WSBridgeCameraImageType.JPEG ? "image/jpeg" : "image/png";
        const imageBytes = new Uint8Array(frameBuffer, imageByteStart, imageByteLength);
        const imageBlob = new Blob([imageBytes], { type: imageMimeType });
        //
        const useImageBitmap = true;
        if(useImageBitmap) {
          frame.imageBitmap = await createImageBitmap(imageBlob);
        } else {
          frame.image = await this.createImage(imageBlob);
        }
        this.queueCameraData.push(frame);
      }
    } catch (error) {
      console.error(error);
    }
  }

  private async createImage(blob:Blob) {
    return new Promise<HTMLImageElement>((resolve, reject) => {
      const url = URL.createObjectURL(blob);
      const image = new Image();
      image.src = url;
      image.onload = () => {
        URL.revokeObjectURL(url);
        resolve(image);
      }
      image.onerror = (error) => {
        URL.revokeObjectURL(url);
        reject(error);
      };
    });
  }

  //---------------------------------------------------------- sync frame.
  private async frameStart() {
    if(this.options) {
      if(this.options.frameStartCallback) {
        this.options.frameStartCallback();
      }
    }
    this.processQueueCameraData();
    this.processQueuePlaneData();
    this.processQueueMeshData();
  }

  private processQueueCameraData() {
    // starting with the most recent frames in the frameQueue,
    // find the first frame with valid metadata (imageBitmap and image are optional),
    // then remove that frame + all previous frames from the queue.
    // any frames after the valid frame are left in the queue, 
    // as they are still loading their image.

    let frame: WSBridgeFrameData | null = null;
    for( let i=this.queueCameraData.length - 1; i>=0; i-- ) {
      const frameI = this.queueCameraData[i];
      if( frame ) { // frame already found. kill all previous frames.
        this.frameCameraDataKill( frameI );
        this.queueCameraData.splice(i, 1);
      } else {
        const valid = frameI && frameI.metadata;
        if( valid ) {
          frame = frameI;
          this.queueCameraData.splice(i, 1); // remove frame from queue.
        }
      }
    }
    if( !frame ) {
      return;
    }
    if( window.EJUpdateCameraData ) {
      window.EJUpdateCameraData(frame.metadata!.cameraData);
    }
    if( frame.imageBitmap ) { 
      this.updateCameraCanvas( frame.imageBitmap );
    } else if( frame.image ) {
      this.updateCameraCanvas( frame.image );
    }
    if( this.options ) {
      if( this.options.cameraUpdateCallback ) {
        this.options.cameraUpdateCallback(frame.metadata!.cameraData!, frame.imageBitmap || frame.image!);
      }
    }
    this.frameCameraDataKill( frame );
  }

  private frameCameraDataKill( frame: WSBridgeFrameData ) {
    if( frame.imageBitmap ) {
      frame.imageBitmap.close();
      frame.imageBitmap = null;
    }
    if( frame.image ) {
      frame.image = null;
    }
  }

  private processQueuePlaneData() {
    for( let i=0; i<this.queuePlaneData.length; i++ ) {
      const frame = this.queuePlaneData[i];
      this.updatePlaneData(frame.metadata!.planeData);
    }
    this.queuePlaneData = [];
  }

  private processQueueMeshData() {
    for( let i=0; i<this.queueMeshData.length; i++ ) {
      const frame = this.queueMeshData[i];
      this.updateMeshData(frame.metadata!.meshData);
    }
    this.queueMeshData = [];
  }

  private async frameEnd() {
    if(this.options) {
      if(this.options.frameEndCallback) {
        this.options.frameEndCallback();
      }
    }
  }

  private updateCameraCanvas( image: HTMLImageElement | ImageBitmap ) {
    const isImage = image instanceof HTMLImageElement;
    const isImageBitmap = image instanceof ImageBitmap;
    const imageW = isImage ? image.naturalWidth : image.width;
    const imageH = isImage ? image.naturalHeight : image.height;
    const sizeChanged = (this.cameraCanvas.width !== imageW) || (this.cameraCanvas.height !== imageH);
    if( sizeChanged ) {
      this.cameraCanvas.width = imageW;
      this.cameraCanvas.height = imageH;
    }
    //
    const useBitmaprendererContext = true;
    if( useBitmaprendererContext && isImageBitmap ) {
      // transfer imageBitmap to canvas using bitmaprenderer context.
      // this is faster than drawing the image to the canvas using 2d context.
      const ctx = this.getCameraContextBitmap();
      ctx.transferFromImageBitmap(image);
    } else {
      const ctx = this.getCameraContext2d();
      ctx.drawImage(image, 0, 0, imageW, imageH);
      //
      const tintBlue = false;
      if( tintBlue ) {
        ctx.globalAlpha = 0.3;
        ctx.fillStyle = 'blue';
        ctx.fillRect(0, 0, imageW, imageH);
        ctx.globalAlpha = 1.0;
      }
    }
    this.cameraCanvasUpdated = true;
  }

  private getCameraContextBitmap(): ImageBitmapRenderingContext {
    if (!this.cameraContextBitmap) {
      this.cameraContextBitmap = this.cameraCanvas.getContext('bitmaprenderer');
    }
    return this.cameraContextBitmap!;
  }
  
  private getCameraContext2d(): CanvasRenderingContext2D {
    if (!this.cameraContext2d) {
      this.cameraContext2d = this.cameraCanvas.getContext('2d');
    }
    return this.cameraContext2d!;
  }

  //---------------------------------------------------------- plane / mesh anchors.
  private updatePlaneData( newData?: WSBridgePlaneData[] ) {
    if( !newData ) {
      return;
    }
    const { anchorsAdded, anchorsUpdated, anchorsRemoved } = this.updateAnchorData(this._planeData, newData);
    //
    if( this.options ) {
      if( this.options.planeAddedCallback && anchorsAdded.length > 0 ) {
        this.options.planeAddedCallback(anchorsAdded);
      }
      if( this.options.planeUpdatedCallback && anchorsUpdated.length > 0 ) {
        this.options.planeUpdatedCallback(anchorsUpdated);
      }
      if( this.options.planeRemovedCallback && anchorsRemoved.length > 0 ) {
        this.options.planeRemovedCallback(anchorsRemoved);
      }
    }
  }

  private updateMeshData( newData?: WSBridgeMeshData[] ) {
    if( !newData ) {
      return;
    }
    const { anchorsAdded, anchorsUpdated, anchorsRemoved } = this.updateAnchorData(this._meshData, newData);
    //
    if( this.options ) {
      if( this.options.meshAddedCallback && anchorsAdded.length > 0 ) {
        this.options.meshAddedCallback(anchorsAdded);
      }
      if( this.options.meshUpdatedCallback && anchorsUpdated.length > 0 ) {
        this.options.meshUpdatedCallback(anchorsUpdated);
      }
      if( this.options.meshRemovedCallback && anchorsRemoved.length > 0 ) {
        this.options.meshRemovedCallback(anchorsRemoved);
      }
    }
  }

  private updateAnchorData( oldData: WSBridgeAnchorData[], newData: WSBridgeAnchorData[] ): { anchorsAdded: WSBridgeAnchorData[], anchorsUpdated: WSBridgeAnchorData[], anchorsRemoved: WSBridgeAnchorData[] } {
    const anchorsAdded: WSBridgeAnchorData[] = [];
    const anchorsUpdated: WSBridgeAnchorData[] = [];
    const anchorsRemoved: WSBridgeAnchorData[] = [];
    //
    const newDataMap = new Map(newData.map(datum => [datum.uuid, datum]));
    for( let i=oldData.length-1; i>=0; i-- ) {
      const oldDataI = oldData[i];
      const newDataI = newDataMap.get(oldDataI.uuid);
      if( newDataI ) {
        if( newDataI.changed ) {
          Object.assign( oldDataI, newDataI );
          anchorsUpdated.push( oldDataI );
        }
        newDataMap.delete(oldDataI.uuid);
      } else {
        oldData.splice(i, 1);
        anchorsRemoved.push(oldDataI);
      }
    }
    for (const newDataI of newDataMap.values()) { // remaining items in newDataMap are new anchors.
      oldData.push(newDataI);
      anchorsAdded.push(newDataI);
    }
    return { anchorsAdded, anchorsUpdated, anchorsRemoved };
  }
}
